function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

const SVG_EXPORT_STYLE_PROPS = [
  'display',
  'visibility',
  'opacity',
  'fill',
  'fill-opacity',
  'stroke',
  'stroke-opacity',
  'stroke-width',
  'stroke-linecap',
  'stroke-linejoin',
  'paint-order',
  'vector-effect',
  'filter',
  'font-family',
  'font-size',
  'font-weight',
  'font-style',
  'letter-spacing',
  'text-anchor',
  'dominant-baseline'
];

function inlineSvgComputedStyles(sourceNode, clonedNode) {
  if (!(sourceNode instanceof Element) || !(clonedNode instanceof Element)) {
    return;
  }

  const computedStyles = window.getComputedStyle(sourceNode);

  SVG_EXPORT_STYLE_PROPS.forEach(property => {
    const value = computedStyles.getPropertyValue(property);

    if (value) {
      clonedNode.style.setProperty(property, value);
    }
  });

  const sourceChildren = Array.from(sourceNode.children);
  const clonedChildren = Array.from(clonedNode.children);

  sourceChildren.forEach((child, index) => {
    inlineSvgComputedStyles(child, clonedChildren[index]);
  });
}

export function formatPdfReportDate(value = new Date()) {
  return new Intl.DateTimeFormat('es-CO', {
    dateStyle: 'long',
    timeStyle: 'short'
  }).format(value);
}

export function formatPdfFileDate(value = new Date()) {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  const hours = String(value.getHours()).padStart(2, '0');
  const minutes = String(value.getMinutes()).padStart(2, '0');

  return `${year}${month}${day}-${hours}${minutes}`;
}

export function drawPdfCard(
  doc,
  x,
  y,
  width,
  height,
  { title, value, helper = '', valueSize = 24 }
) {
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(204, 233, 245);
  doc.roundedRect(x, y, width, height, 16, 16, 'FD');

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(90, 118, 136);
  doc.text(String(title), x + 14, y + 18);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(valueSize);
  doc.setTextColor(23, 50, 71);
  doc.text(String(value), x + 14, y + 44);

  if (helper) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(90, 118, 136);
    const helperLines = doc.splitTextToSize(helper, width - 28);
    const helperStartY =
      y + height - 16 - Math.max(helperLines.length - 1, 0) * 10;
    doc.text(helperLines, x + 14, helperStartY);
  }
}

export function drawPdfDistributionCard(
  doc,
  x,
  y,
  width,
  height,
  {
    title,
    items = [],
    accentColor = [26, 168, 223],
    emptyMessage = 'Sin datos disponibles.',
    maxItems = 5
  }
) {
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(204, 233, 245);
  doc.roundedRect(x, y, width, height, 16, 16, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(23, 50, 71);
  doc.text(String(title), x + 14, y + 18);

  const visibleItems = Array.isArray(items) ? items.slice(0, maxItems) : [];

  if (visibleItems.length === 0) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(90, 118, 136);
    const lines = doc.splitTextToSize(emptyMessage, width - 28);
    doc.text(lines, x + 14, y + 40);
    return;
  }

  const maxCount = Math.max(...visibleItems.map(item => Number(item?.count) || 0), 0);
  const rowHeight = 28;
  const firstRowY = y + 42;

  visibleItems.forEach((item, index) => {
    const rowTop = firstRowY + index * rowHeight;
    const count = Number(item?.count) || 0;
    const label = String(item?.label ?? '').trim() || 'Sin dato';
    const truncatedLabel = doc.splitTextToSize(label, width - 86)[0] || label;
    const barWidth = Math.max(
      ((width - 28) * (maxCount > 0 ? count / maxCount : 0)),
      count > 0 ? 18 : 0
    );

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(59, 90, 108);
    doc.text(truncatedLabel, x + 14, rowTop);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(23, 50, 71);
    doc.text(String(count), x + width - 14, rowTop, {
      align: 'right'
    });

    doc.setFillColor(217, 231, 239);
    doc.roundedRect(x + 14, rowTop + 8, width - 28, 6, 4, 4, 'F');
    doc.setFillColor(...accentColor);
    doc.roundedRect(x + 14, rowTop + 8, barWidth, 6, 4, 4, 'F');
  });
}

export async function svgElementToPngDataUrl(svgElement, targetWidth = 1200) {
  if (!svgElement) {
    throw new Error('SVG element is required');
  }

  const clonedSvg = svgElement.cloneNode(true);
  const viewBoxWidth =
    svgElement.viewBox?.baseVal?.width || svgElement.clientWidth || targetWidth;
  const viewBoxHeight =
    svgElement.viewBox?.baseVal?.height || svgElement.clientHeight || targetWidth * 0.7;

  clonedSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  clonedSvg.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
  clonedSvg.setAttribute('width', String(viewBoxWidth));
  clonedSvg.setAttribute('height', String(viewBoxHeight));
  clonedSvg.style.setProperty('background', '#ffffff');

  inlineSvgComputedStyles(svgElement, clonedSvg);

  const serializer = new XMLSerializer();
  const svgString = serializer.serializeToString(clonedSvg);
  const svgBlob = new Blob([svgString], {
    type: 'image/svg+xml;charset=utf-8'
  });
  const svgUrl = URL.createObjectURL(svgBlob);

  try {
    const image = await loadImage(svgUrl);
    const outputWidth = Math.round(targetWidth);
    const outputHeight = Math.round((viewBoxHeight / viewBoxWidth) * outputWidth);
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!context) {
      throw new Error('Canvas context is not available');
    }

    canvas.width = outputWidth;
    canvas.height = outputHeight;

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, outputWidth, outputHeight);

    return canvas.toDataURL('image/png');
  } finally {
    URL.revokeObjectURL(svgUrl);
  }
}
